import { Injectable } from '@angular/core';

export interface PermissionModule {
  moduleKey: string;
  access: 'none' | 'view' | 'modify';
}

const MODULE_MAP: Record<string, { parent: string; sub: string }> = {
  'checklist': { parent: 'checklist', sub: 'checklist' },
  'review-checklist': { parent: 'checklist', sub: 'reviewChecklist' },
  'reports': { parent: 'checklist', sub: 'checklistReport' },
  'checklist-notifications': { parent: 'checklist', sub: 'notification' },
  'tickets': { parent: 'ticketManagement', sub: 'tickets' },
  'complaint': { parent: 'ticketManagement', sub: 'task' },
  'raise-complaint': { parent: 'ticketManagement', sub: 'raiseComplaint' },
  'assign-reassign': { parent: 'ticketManagement', sub: 'assignReassign' },
  'parked': { parent: 'ticketManagement', sub: 'parked' },
  'start-complete-work': { parent: 'ticketManagement', sub: 'startCompleteWork' },
  'feedback-resolution': { parent: 'ticketManagement', sub: 'feedbackResolution' },
  'activity-log': { parent: 'ticketManagement', sub: 'activityLog' },
  'escalation-tickets': { parent: 'ticketManagement', sub: 'escalationTickets' },
  'complaint-report': { parent: 'ticketManagement', sub: 'complaintReport' },
  'ticket-notifications': { parent: 'ticketManagement', sub: 'notification' },
  'announcements': { parent: 'announcement', sub: 'createAnnouncement' },
  'notice-board': { parent: 'announcement', sub: 'noticeBoard' },
  'push-checklist': { parent: 'pushNotification', sub: 'checklist' },
  'push-announcement': { parent: 'pushNotification', sub: 'announcement' },
  'push-ticket-all': { parent: 'pushNotification', sub: 'ticketAll' },
  'push-escalation': { parent: 'pushNotification', sub: 'escalation' },
  'push-assigned': { parent: 'pushNotification', sub: 'assigned' },
  'push-ticket-raised': { parent: 'pushNotification', sub: 'ticketRaised' },
  'notifications': { parent: 'checklist', sub: 'notification' } // fallback
};

@Injectable({ providedIn: 'root' })
export class PermissionService {

  private get profile(): any {
    try {
      return JSON.parse(localStorage.getItem('UserProfileData') || '{}');
    } catch {
      return {};
    }
  }

  private get permissions(): any {
    const p = this.profile?.result?.permissions;
    if (p && (p.app || p.panel?.length > 0)) return p;
    return { app: {}, panel: [] };
  }

  get userRole(): string {
    return this.profile?.result?.role || '';
  }

  get isSuperAdmin(): boolean {
    return ['superadmin', 'admin', 'adminuser'].includes(this.userRole.toLowerCase());
  }

  /** Check if the user has 'modify' access to a panel module */
  canAccessPanelModule(moduleKey: string): boolean {
    if (this.isSuperAdmin) return true;
    const list = this.permissions.panel || [];
    const entry = list.find((m: any) => m.moduleKey === moduleKey);
    return entry?.access === 'modify';
  }

  /** Check if the user has 'modify' access to an app module */
  canAccessAppModule(moduleKey: string): boolean {
    if (this.isSuperAdmin) return true;
    const app = this.permissions.app || [];

    // Backward compatibility for flat array
    if (Array.isArray(app)) {
      for (const item of app) {
        if ('children' in item && Array.isArray(item.children)) {
          const child = item.children.find((c: any) => c.moduleKey === moduleKey);
          if (child) return child.access === 'modify';
        } else if ((item as any).moduleKey === moduleKey) {
          return (item as any).access === 'modify';
        }
      }
      return false;
    }

    // New nested object format
    const mapping = MODULE_MAP[moduleKey];
    if (mapping) {
      const parentObj = app[mapping.parent];
      if (!parentObj) return false;
      const childObj = parentObj.subModule?.[mapping.sub];
      return childObj?.modify === true;
    }

    return false;
  }

  /** Returns all allowed panel module keys */
  get allowedPanelModules(): string[] {
    if (this.isSuperAdmin) return [
      'task-category','task','checklist-listing','report',
      'user-list','complaint-register','complaint-report','announcements','roles'
    ];
    return (this.permissions.panel || [])
      .filter((m: any) => m.access === 'modify')
      .map((m: any) => m.moduleKey);
  }

  /** Returns all allowed app module keys */
  get allowedAppModules(): string[] {
    if (this.isSuperAdmin) return [
      // Checklist Module
      'checklist', 'review-checklist', 'reports',
      // Ticket Management Module
      'tickets', 'complaint', 'raise-complaint', 'assign-reassign',
      'parked', 'start-complete-work', 'feedback-resolution',
      'activity-log', 'escalation-tickets', 'complaint-report',
      // Announcement Module
      'announcements', 'notice-board',
      // Push Notification Module
      'push-checklist', 'push-announcement', 'push-ticket-all',
      'push-escalation', 'push-assigned', 'push-ticket-raised',
      // Shared
      'notifications',
    ];
    
    const allowed: string[] = [];
    const app = this.permissions.app || [];

    // Backward compatibility for flat array
    if (Array.isArray(app)) {
      for (const item of app) {
        if ('children' in item && Array.isArray(item.children)) {
          item.children.forEach((c: any) => {
            if (c.access !== 'none') {
              allowed.push(c.moduleKey);
            }
          });
        } else if ((item as any).moduleKey && (item as any).access !== 'none') {
          allowed.push((item as any).moduleKey);
        }
      }
    } else {
      // New nested object format
      Object.keys(MODULE_MAP).forEach(key => {
        const mapping = MODULE_MAP[key];
        const parentObj = app[mapping.parent];
        if (parentObj) {
          const childObj = parentObj.subModule?.[mapping.sub];
          if (childObj && (childObj.view || childObj.modify)) {
            allowed.push(key);
          }
        }
      });
    }
    return allowed;
  }

  /** Check if the user has at least 'view' access to an app module */
  canViewAppModule(moduleKey: string): boolean {
    if (this.isSuperAdmin) return true;
    const app = this.permissions.app || [];

    // Backward compatibility for flat array
    if (Array.isArray(app)) {
      for (const item of app) {
        if ('children' in item && Array.isArray(item.children)) {
          const child = item.children.find((c: any) => c.moduleKey === moduleKey);
          if (child) return child.access === 'view' || child.access === 'modify';
        } else if ((item as any).moduleKey === moduleKey) {
          return (item as any).access === 'view' || (item as any).access === 'modify';
        }
      }
      return false;
    }

    // New nested object format
    const mapping = MODULE_MAP[moduleKey];
    if (mapping) {
      const parentObj = app[mapping.parent];
      if (!parentObj) return false;
      const childObj = parentObj.subModule?.[mapping.sub];
      return childObj?.view === true || childObj?.modify === true;
    }

    return false;
  }

  /** Check if the user has at least 'view' access to a panel module */
  canViewPanelModule(moduleKey: string): boolean {
    if (this.isSuperAdmin) return true;
    const list = this.permissions.panel || [];
    const entry = list.find((m: any) => m.access === 'modify' && m.moduleKey === moduleKey);
    return !!entry;
  }
}
